import { toast } from "@jsq/ds/core";
import {
  useCreateBulkArenaAccessMutation,
  useEndBulkArenaAccessMutation,
} from "~/graphql/supergraphHooks";
import { JsqAccessTypeEnum } from "~/graphql/supergraphTypes";
import { toGlobalId } from "~/utils/graphql-helpers";

type Args = { userId: string };

export type CreateBulkArenaAccessParams = {
  userId: string;
  roleGroupName: string;
  accessType: JsqAccessTypeEnum;
  endDate: string | null;
};

export const useBulkArenaAccess = ({ userId }: Args) => {
  const [createBulkArenaAccessMutation, { loading: isCreating }] =
    useCreateBulkArenaAccessMutation();
  const [endBulkArenaAccessMutation, { loading: isEnding }] = useEndBulkArenaAccessMutation();

  const createBulkArenaAccess = async ({
    roleGroupName,
    accessType,
    endDate,
  }: Omit<CreateBulkArenaAccessParams, "userId">) => {
    try {
      const { data } = await createBulkArenaAccessMutation({
        variables: {
          input: {
            userId: toGlobalId("UserObjectType", userId),
            roleGroupName,
            accessType,
            endDate,
          },
        },
        refetchQueries: ["ArenaAccessForUser", "ActiveArenaAccesses", "GetCCToolsUsers"],
        awaitRefetchQueries: true,
      });

      if (data?.createBulkArenaAccess?.__typename === "CreateBulkArenaAccessResponse") {
        toast.success("Access to all client workspaces enabled");
        return true;
      } else if (data?.createBulkArenaAccess?.__typename === "MutationErrorResponse") {
        toast.error(data.createBulkArenaAccess.message);
      } else {
        toast.error("Failed to enable access to all client workspaces");
      }
      return false;
    } catch {
      toast.error("Failed to enable access to all client workspaces");
      return false;
    }
  };

  const endBulkArenaAccess = async () => {
    try {
      const { data } = await endBulkArenaAccessMutation({
        variables: {
          input: {
            userId: toGlobalId("UserObjectType", userId),
          },
        },
        refetchQueries: ["ArenaAccessForUser", "ActiveArenaAccesses", "GetCCToolsUsers"],
        awaitRefetchQueries: true,
      });

      if (data?.endBulkArenaAccess?.__typename === "EndBulkArenaAccessResponse") {
        toast.success("Access to all client workspaces disabled");
        return true;
      } else if (data?.endBulkArenaAccess?.__typename === "MutationErrorResponse") {
        toast.error(data.endBulkArenaAccess.message);
      } else {
        toast.error("Failed to disable access to all client workspaces");
      }
      return false;
    } catch {
      toast.error("Failed to disable access to all client workspaces");
      return false;
    }
  };

  return {
    loading: isCreating || isEnding,
    createBulkArenaAccess,
    endBulkArenaAccess,
  };
};
